import { inject, Injectable } from '@angular/core';
import { LenderService } from '@shared/service/lender/lender.service';
import { PrimitiveType } from '../../class/workflow.type';
import { ConfigService } from '../config/config.service';
import { StorageService } from '../storage/storage.service';
import { WorkflowWidgetConfigService } from '../../widget/workflow-widget-config.service';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { StatefulService } from '@app/service/stateful.service';
import { FieldService } from '@workflow/class/field.service';

/**
 * Resolve references in strings
 */
@Injectable({
  providedIn: 'root'
})
export class StringVariableService {
  private readonly statefulService = inject(StatefulService);
  private readonly fieldService = inject(FieldService);

  constructor(
    private readonly configService: ConfigService,
    private readonly lenderService: LenderService,
    private readonly storageService: StorageService,
    private readonly workflowWidgetConfigService: WorkflowWidgetConfigService,
    private readonly adminFeaturesService: AdminPortalFeaturesService
  ) {}

  /**
   * Returns processed string (or original value for every other type)
   * @param arg string with var reference
   * @param iterationId optional index to attach to array items in absence of '@IDX' at the end of the ref
   * @param pageIterationStr optional index to of iterated subpage
   */
  public parse(arg: PrimitiveType, iterationId?: number, pageIterationId?: number, defaultValue = '', addCommas = false): PrimitiveType {
    if (typeof arg !== 'string') {
      return arg;
    }
    let ref: string;
    let processed = arg;
    while (true) {
      ref = this.matchVar(processed);
      if (!ref) {
        break;
      }
      // tslint:disable-next-line: no-magic-numbers
      const fullPath = ref.substring(2, ref.length - 1);
      const [protocol, ...parts] = fullPath.split(':');
      const [path, iterationStr] = parts.join(':').split('@');
      const idx = iterationStr != null && !Number.isNaN(+iterationStr) ? +iterationStr : iterationId;

      const pageIterationIndex = pageIterationId;

      let value: PrimitiveType;
      if (this.storageService.backends[protocol]) {
        value = this.statefulService.wrap(fullPath, idx, pageIterationIndex).value;
      } else {
        value = this.processProtocol(protocol, addCommas, path, idx, pageIterationIndex);
      }
      if (ref === processed) {
        return value;
      }
      processed = processed.replace(ref, String(value || defaultValue));
    }

    return processed;
  }

  /**
   * Helper method for above.
   * @param protocol the protocol from above method
   * @param addCommas Whether or not to add commas to numeric values.
   * @param path the path
   * @param idx the index
   * @param pageIterationIndex the page iteration index
   * @returns the value needed above
   */
  private processProtocol(protocol: string, addCommas: boolean, path, idx, pageIterationIndex): PrimitiveType {
    let value: PrimitiveType;

    if (protocol === 'field') {
      value = this.fieldService.createField(path, this.workflowWidgetConfigService.flowId, idx, pageIterationIndex).data.value;
    } else if (protocol.startsWith('payload')) {
      // When protocol is like payload.xyz:field-name-id
      const paramName = protocol.substring(protocol.indexOf('.') + 1);
      value = this.fieldService.createField(path, this.workflowWidgetConfigService.flowId, idx, pageIterationIndex).payload[paramName];
      // eslint-disable-next-line eqeqeq
      if (addCommas && value == parseInt(value as string)) {
        value = Number('' + value).toLocaleString();
      }
    } else if (protocol === 'config') {
      value = this.configService.read(path, true) as PrimitiveType;
    } else if (protocol === 'lenderData') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value = this.configService.read(path, true, this.lenderService.lenderDataInfo as any) as PrimitiveType;
    } else if (protocol === 'widget') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value = this.configService.read(path, true, this.workflowWidgetConfigService.customConfig as any) as PrimitiveType;
    } else if (protocol === 'admin') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value = this.configService.read(path, true, this.adminFeaturesService.config as any) as PrimitiveType;
    }
    return value;
  }

  /**
   * Finds the leftmost, deepest nested variable
   * Example order of execution:
   *      3        1                 2                   4
   * 'fhs #{api:x/y#{config:lol/bol}/#{mem:fdsf}z} hsdhf #{ss:f/f/fds} kdjs hfkdj hfdksjh fkj hfhkjds hhjkdshkjhdk';
   * @param processible string to find variable in
   */
  private matchVar(processible: string): string {
    const left = processible.indexOf('#{');
    if (left > -1) {
      // tslint:disable-next-line: no-magic-numbers
      const subStr = processible.substr(left + 2);
      const subLeft = subStr.indexOf('#{');
      const right = subStr.indexOf('}');
      if (right > -1) {
        if (subLeft > -1 && subLeft < right) {
          return this.matchVar(subStr);
        } else {
          return '#{' + subStr.substr(0, right + 1);
        }
      }
    }
  }
}
